/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { RefObject, useEffect, useRef } from "react";

const on = (
  obj: Document | HTMLElement,
  ...args: Parameters<typeof window.addEventListener>
) => obj.addEventListener(...args);

const off = (
  obj: Document | HTMLElement,
  ...args: Parameters<typeof window.addEventListener>
) => obj.removeEventListener(...args);

export const useClickOutside = <E extends Event>(
  ref: RefObject<HTMLElement | null>,
  onClickAway: (event: E) => void,
  active = true
) => {
  const savedCallback = useRef(onClickAway);

  useEffect(() => {
    savedCallback.current = onClickAway;
  });
  useEffect(() => {
    if (active) {
      const handler = (event: E) => {
        const { current: el } = ref;
        if (el instanceof HTMLElement && event.target instanceof HTMLElement) {
          !el.contains(event.target) && savedCallback.current(event);
        }
      };

      on(document, "mousedown", handler as EventListener);

      return () => {
        off(document, "mousedown", handler as EventListener);
      };
    }
  }, [ref, active]);
};
